import React from 'react'
import type { Rule } from 'antd/lib/form'
import { useTranslation } from 'next-i18next'
import { isEmailValid } from 'src/core/helpers'
import { PlusOutlined } from '@ant-design/icons'
import styles from './CustomTextInput.module.scss'
import { Input, Form, Tooltip, Button, InputProps } from 'antd'

interface CustomTextInputProps extends InputProps {
  name: string,
  required?: boolean,
  placeholder?: string,
  type?: string,
  handleToggleClick?: () => void,
  toogleText?: string,
  disabled?: boolean,
  noSpacedAllowed?: boolean
  minLengthAllowed?: number
  // eslint-disable-next-line no-unused-vars
  onChange?: (value: React.ChangeEvent<HTMLInputElement>) => void
}

const CustomTextInput: React.FC<CustomTextInputProps> = (props) => {
  const { t } = useTranslation()
  const {
    name, required = false, placeholder,
    type = null,
    handleToggleClick = null,
    toogleText = '',
    disabled = false,
    onChange = null,
    noSpacedAllowed = false,
    minLengthAllowed = null,
    ...restProps
  } = props

  const handleOnChange = (value: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange !== null) {
      onChange(value)
    }
  }

  const tooltipButton = (
    <Tooltip title={toogleText} color={'blue'}>
      <Button
        type='primary'
        htmlType='submit'
        shape='circle'
        onClick={handleToggleClick}
        className='radius-100'
        icon={<PlusOutlined />}
      />
    </Tooltip>
  )

  const customEmailValidation = (value: string) => {
    if (!required) {
      return Promise.resolve()
    } else if (isEmailValid(value)) {
      return Promise.resolve()
    } else { return Promise.reject(t('general.InvalidEmail')) }
  }

  const customSpaceValidation = (value: string) => {
    if (/\s/g.test(value)) {
      return Promise.reject(t('general.SpaceValidation'))
    } else {
      return Promise.resolve()
    }
  }

  const customMinLengthValidation = (value: string) => {
    if (value?.trim().length < minLengthAllowed) {
      return Promise.reject(t('Vouchers.LengthValidation', { length: minLengthAllowed }))
    } else {
      return Promise.resolve()
    }
  }

  const customUrlValidation = (value: string) => {
    const urlRegex = /^(ftp|http|https):\/\/[^\s/$.?#]+\.[^\s]+$/
    if (!required) {
      return Promise.resolve()
    } else if (urlRegex.test(value)) {
      return Promise.resolve()
    } else {
      return Promise.reject('Please enter a valid URL')
    }
  }

  const getRules = (): Rule[] => {
    const rules: Rule[] = [{ required, message: t('This field is required'), whitespace: true }]
    if (type === 'email') {
      rules.push({
        message: t('general.InvalidEmail'),
        validator: (_: unknown, value: string) => customEmailValidation(value)
      })
    }
    if (noSpacedAllowed) {
      rules.push({
        validator: (_: unknown, value: string) => customSpaceValidation(value)
      })
    }
    if (minLengthAllowed) {
      rules.push({
        validator: (_: unknown, value: string) => customMinLengthValidation(value)
      })
    }
    if (type === 'url') {
      rules.push({
        message: 'Please enter a valid URL',
        validator: (_: unknown, value: string) => customUrlValidation(value)
      })
    }
    return rules
  }

  return (
    <Form.Item
      name={name}
      rules={getRules()}
    >
      <Input
        placeholder={placeholder}
        size='large'
        onChange={handleOnChange}
        className={styles['custom-input']}
        addonAfter={handleToggleClick ? tooltipButton : null}
        disabled={disabled}
        {...restProps}
      />
    </Form.Item>
  )
}

export default CustomTextInput
